import { t } from 'i18next';
import { path, propOr, mergeDeepLeft, equals } from 'ramda';
import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import useDetectDiffInChanges from '../../../utils/hooks/useDetectDiffInChanges';
import { useForm } from '../../../utils/hooks/useForm';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import { filterNames } from '../../constants/filters';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { SelectS, TextInput, Radio } from '../common';

import FieldComment from './common/fieldComment';
import SaveBtn from './common/saveBtn';
import { CompanyMainInformationEditWrapper, DataRowWrapS, InputWrapper, SaveButtonWrapper } from './companyEditS';
import WorkingHours from './workingHours';

const AddressSection = ({ values, errors, touched, handleChange, handleBlur, activeFilters }) => (
  <DataRowWrapS justify="space-between">
    <InputWrapper maxWidth="22%">
      <SelectS
        height="auto"
        label={t('companyEdit.labels.canton')}
        name="canton"
        value={values.canton}
        onChange={handleChange}
        onBlur={handleBlur}
        isError={path(['canton'], errors) && path(['canton'], touched)}
        error={path(['canton'], errors)}
      >
        <option value="">{t('companyEdit.selectPlaceholders.canton')}</option>
        {activeFilters[filterNames.KANTONES] &&
          activeFilters[filterNames.KANTONES]
            .sort()
            .filter((el) => el !== 'Alle') // alle goes from server drop.
            .map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
      </SelectS>
    </InputWrapper>

    <InputWrapper width="22%">
      <TextInput
        label={t('companyEdit.labels.city')}
        name="base.address.city"
        type="text"
        value={path(['base', 'address', 'city'], values)}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </InputWrapper>

    <InputWrapper width="22%">
      <TextInput
        label={t('companyEdit.labels.street')}
        name="base.address.street"
        type="text"
        placeholder={t('companyEdit.labels.streetPlaceholder')}
        value={path(['base', 'address', 'street'], values)}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </InputWrapper>

    <InputWrapper width="22%">
      <InputMask
        mask="9999"
        maskChar="X"
        value={path(['base', 'address', 'zip'], values)}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        {(maskedInputProps) => (
          <TextInput
            label={t('companyEdit.labels.zip')}
            name="base.address.zip"
            type="text"
            isError={path(['base', 'address', 'zip'], errors) && path(['base', 'address', 'zip'], touched)}
            error={path(['base', 'address', 'zip'], errors)}
            {...maskedInputProps}
          />
        )}
      </InputMask>
    </InputWrapper>
  </DataRowWrapS>
);

const AdditionalDetailsSection = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  staticCompanyValues,
  windowWidth,
}) => (
  <>
    <DataRowWrapS justify="space-between">
      <InputWrapper width="22%">
        <InputMask
          mask="\099 999 99 99"
          maskChar="X"
          value={path(['base', 'tel'], values)}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          {(maskedInputProps) => (
            <TextInput
              label={t('companyEdit.labels.phone')}
              name="base.tel"
              type="text"
              placeholder="043 444 44 44"
              isError={path(['base', 'tel'], errors) && path(['base', 'tel'], touched)}
              error={path(['base', 'tel'], errors)}
              {...maskedInputProps}
            />
          )}
        </InputMask>
      </InputWrapper>

      <InputWrapper width="22%">
        <InputMask
          mask="9999"
          maskChar="X"
          value={path(['yearOfFoundation'], values)}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          {(maskedInputProps) => (
            <TextInput
              label={t('companyEdit.labels.yearOfFoundation')}
              name="yearOfFoundation"
              type="text"
              {...maskedInputProps}
            />
          )}
        </InputMask>
      </InputWrapper>

      <InputWrapper width="22%">
        <TextInput
          label={t('companyEdit.labels.employeeCount')}
          name="employeeCount"
          type="text"
          value={path(['employeeCount'], values)}
          onChange={handleChange}
          onBlur={handleBlur}
          isError={path(['employeeCount'], errors) && path(['employeeCount'], touched)}
          error={path(['employeeCount'], errors)}
        />
      </InputWrapper>

      <InputWrapper width="22%" />
    </DataRowWrapS>

    <DisplayFlexS flexWrapScreenTablet="wrap" margin="0 0 20px 0" direction="row" directionScreenMedium="column">
      <Radio
        radioGroupLabel={t('companyEdit.labels.communicationPreferences')}
        transKeyBase="companyEdit"
        name="communicationPreferences"
        options={staticCompanyValues.communicationPreferences}
        initialState={`${values.communicationPreferences}`}
        onChange={(updated) => setFieldValue('communicationPreferences', updated, false)}
      />
      {windowWidth > SCREEN_SIZES.MEDIUM && <FieldComment transPath="companyEdit.fieldComment.main" maxWidth="45.5%" />}
    </DisplayFlexS>

    {values.workingHours && (
      <DisplayFlexS margin="0" marginScreenMedium="19px 0 0 0" directionScreenMedium="column">
        <WorkingHours
          onWorkingHoursChange={(updatedWorkingHours) => setFieldValue('workingHours', updatedWorkingHours, false)}
          initialState={values.workingHours}
        />
      </DisplayFlexS>
    )}
  </>
);

const getInitialValues = (company) => ({
  canton: propOr('', 'canton', company),
  base: {
    tel: path(['base', 'tel'], company) || '',
    address: {
      city: path(['base', 'address', 'city'], company) || '',
      street: path(['base', 'address', 'street'], company) || '',
      zip: path(['base', 'address', 'zip'], company) || '',
    },
  },
  communicationPreferences: company.communicationPreferences || '',
  workingHours: company.workingHours,
  employeeCount: company.employeeCount || '',
  yearOfFoundation: company.yearOfFoundation || '',
  //mehrwertsteuerNum: company.mehrwertsteuerNum || '',
});

const normalizeData = (values) => {
  return {
    ...values,
    base: {
      ...values.base,
      address: {
        city: path(['base', 'address', 'city'], values).trim(),
        street: path(['base', 'address', 'street'], values).trim(),
        zip: path(['base', 'address', 'zip'], values),
      },
      tel: values.base.tel.replace(/ /g, ''),
    },
  };
};

const validationSchema = Yup.object().shape({
  employeeCount: Yup.number()
    .positive(t('companyEdit.errorMessages.employeeCount.notLessOne'))
    .typeError(t('companyEdit.errorMessages.employeeCount.type')),
});

const MainInformationSettings = ({ isAdmin, updateCompany, setActiveSettingType }) => {
  const dispatch = useDispatch();
  const activeFilters = useSelector((state) => state.app.activeFilters);
  const company = useSelector((state) => state.company.companyItem);
  const loading = useSelector((state) => state.company.loading);
  const staticCompanyValues = useSelector((state) => state.app.staticCompanyValues);
  const { width: windowWidth } = useWindowSize();

  const [initialValues, setInitialValues] = useState(getInitialValues(company));

  useEffect(() => {
    setInitialValues(getInitialValues(company));
  }, [company]);

  const handleSubmit = (values) => {
    const normalizedData = normalizeData(values);
    dispatch(updateCompany(mergeDeepLeft(normalizedData, company), isAdmin));
  };

  const {
    values,
    errors,
    touched,
    onChange: handleChange,
    onBlur: handleBlur,
    onSubmit,
    dirty,
    setFieldValue,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const hasChanges = (values) => !equals(initialValues, values);

  useDetectDiffInChanges(() => hasChanges(values), onSubmit, setActiveSettingType);

  return (
    <CompanyMainInformationEditWrapper>
      <form onSubmit={onSubmit}>
        <DisplayFlexS direction="column" directionScreenMedium="column">
          <AddressSection
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            activeFilters={activeFilters}
          />
          <AdditionalDetailsSection
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            staticCompanyValues={staticCompanyValues}
            windowWidth={windowWidth}
          />
          <SaveButtonWrapper marginTop="30" marginTopMobile="55">
            <SaveBtn loading={loading} disabled={!dirty && !hasChanges(values)} />
          </SaveButtonWrapper>
        </DisplayFlexS>
      </form>
    </CompanyMainInformationEditWrapper>
  );
};

export default MainInformationSettings;
